import type { HTMLAttributes } from 'react';
import type { CSSObject } from 'styled-components';
import styled from 'styled-components';

import style from '../../../style';
import type Theme from '../../../style/types/theme';
import { Size } from '../constants';
import { Variant } from './constants';
import type { ColorStyles } from './types';

const getContentStyles = (size: Size, theme: Theme, variant: Variant): CSSObject => {
  switch (size) {
    case Size.BIG:
      return {
        padding: `${theme.size.units(1)} ${theme.size.units(3)}`,
        fontWeight: 'bold',
        fontSize: theme.size.units(2.25),
        lineHeight: theme.size.units(3),
      };
    case Size.SMALL:
      return {
        padding:
          variant === Variant.TEXT
            ? theme.size.units(0.5)
            : `${theme.size.units(0.5)} ${theme.size.units(1.5)}`,
        fontWeight: 'bold',
        fontSize: theme.size.units(1.5),
        lineHeight: theme.size.units(2),
      };
    default:
      return {
        padding:
          variant === Variant.TEXT
            ? `${theme.size.units(0.75)} ${theme.size.units(1)}`
            : `${theme.size.units(0.75)} ${theme.size.units(2)}`,
        fontWeight: 600,
        fontSize: theme.size.units(1.875),
        lineHeight: theme.size.units(2.5),
      };
  }
};

const getColor = (
  variant: Variant,
  disabled: boolean,
  primaryColor?: ColorStyles,
  secondaryColor?: ColorStyles,
): string | undefined => {
  switch (variant) {
    case Variant.TEXT:
      return disabled
        ? primaryColor?.disabled ?? primaryColor?.default
        : primaryColor?.default;
    default:
      return disabled
        ? secondaryColor?.disabled ?? secondaryColor?.default
        : secondaryColor?.default;
  }
};

interface ContentWrapperProps {
  $size: Size;
  $variant: Variant;
  $disabled: boolean;
  $loading: boolean;
  $hasStartIcon?: boolean;
  $hasEndIcon?: boolean;
  $primaryColor?: ColorStyles;
  $secondaryColor?: ColorStyles;
  $ellipsis?: boolean;
}

export const ContentWrapper = styled.div<ContentWrapperProps>(
  ({
    $size,
    $variant,
    $disabled,
    $loading,
    $hasStartIcon,
    $hasEndIcon,
    theme,
    $primaryColor,
    $secondaryColor,
    $ellipsis = false,
  }) => ({
    display: 'flex',
    position: 'relative',
    fontStyle: 'normal',
    fontFamily: theme.font.family,
    color: getColor($variant, $disabled, $primaryColor, $secondaryColor),
    visibility: $loading ? 'hidden' : 'visible',
    transition:
      $variant === Variant.TEXT ? 'color .1s cubic-bezier(.4,0,.4,1)' : undefined,
    ...getContentStyles($size, theme, $variant),
    paddingLeft: $hasStartIcon ? 0 : undefined,
    paddingRight: $hasEndIcon ? 0 : undefined,
    alignItems: 'center',
    maxWidth: $ellipsis ? '100%' : undefined,
  }),
);

const getBackgroundColor = (
  variant: Variant,
  disabled: boolean,
  primaryColor?: ColorStyles,
): string | undefined => {
  switch (variant) {
    case Variant.TEXT:
      return 'transparent';
    case Variant.STROKE:
      return 'transparent';
    case Variant.BACKGROUND:
    default:
      return disabled && primaryColor?.disabled
        ? primaryColor?.disabled
        : primaryColor?.default;
  }
};

const getBackgroundBorder = (
  variant: Variant,
  disabled: boolean,
  primaryColor?: ColorStyles,
) => {
  switch (variant) {
    case Variant.STROKE:
      return disabled
        ? `1px solid ${primaryColor?.disabled}`
        : `1px solid ${primaryColor?.default}`;
    default:
      return 'none';
  }
};

interface BackgroundProps {
  $variant: Variant;
  $disabled: boolean;
  $primaryColor?: ColorStyles;
}

export const Background = styled.div<BackgroundProps>(
  ({ $variant, $disabled, theme, $primaryColor }) => ({
    borderRadius: theme.size.units(5.5),
    backgroundColor: getBackgroundColor($variant, $disabled, $primaryColor),
    border: getBackgroundBorder($variant, $disabled, $primaryColor),
    transform: 'scale(1)',
    transition:
      'background-color .15s cubic-bezier(.4,0,.4,1), transform .1s cubic-bezier(.4,0,.4,1), border-color .15s cubic-bezier(.4,0,.4,1), transform .1s cubic-bezier(.4,0,.4,1)',
  }),
);

const getBackgroundBorderHoverStyles = (
  variant: Variant,
  disabled: boolean,
  primaryColor?: ColorStyles,
): CSSObject => {
  if (disabled) {
    return {};
  }

  switch (variant) {
    case Variant.STROKE:
      return {
        border: `1px solid ${primaryColor?.hover}`,
      };
    default:
      return {};
  }
};

const getContentHoverStyles = (
  variant: Variant,
  disabled: boolean,
  primaryColor?: ColorStyles,
  secondaryColor?: ColorStyles,
): CSSObject => {
  if (disabled) {
    return {};
  }

  switch (variant) {
    case Variant.TEXT:
      return {
        color: primaryColor?.hover,
      };
    case Variant.BACKGROUND:
    default:
      return {
        color: secondaryColor?.hover,
      };
  }
};

const getBackgroundHoverStyles = (
  variant: Variant,
  disabled: boolean,
  loading: boolean,
  primaryColor?: ColorStyles,
): CSSObject => {
  if (loading) {
    return {};
  }

  switch (variant) {
    case Variant.BACKGROUND:
      return {
        backgroundColor: disabled ? primaryColor?.disabled : primaryColor?.hover,
      };
    default:
      return {};
  }
};

const getCursor = (loading: boolean, disabled: boolean) => {
  if (loading) {
    return 'wait';
  }

  if (disabled) {
    return 'not-allowed';
  }

  return 'pointer';
};

const resetButtonStyles = {
  color: 'inherit',
  backgroundColor: 'inherit',
  padding: 0,
  borderWidth: 0,
  borderStyle: 'none',
  borderColor: 'none',
  borderImage: 'none',
  border: 'none',
  borderRadius: 0,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
  borderBottomLeftRadius: 0,
  textDecoration: 'none',
};

export const SpinnerWrapper = styled.div({});

const getIconMargin = (size: Size, theme: Theme) => {
  switch (size) {
    case Size.BIG:
      return `0 ${theme.size.units(1.5)} 0 ${theme.size.units(2)}`;
    case Size.SMALL:
      return `0 ${theme.size.units(0.75)} 0 ${theme.size.units(1)}`;
    default:
      return `0 ${theme.size.units(1)} 0 ${theme.size.units(1.5)}`;
  }
};

const getEndIconMargin = (size: Size, theme: Theme) => {
  switch (size) {
    case Size.BIG:
      return `0 ${theme.size.units(2)} 0 ${theme.size.units(1.5)}`;
    case Size.SMALL:
      return `0 ${theme.size.units(1)} 0 ${theme.size.units(0.75)}`;
    default:
      return `0 ${theme.size.units(1.5)} 0 ${theme.size.units(1)}`;
  }
};

interface StyledIconProps {
  $defaultIconColor?: string;
}

export const StyledIcon = styled.i<StyledIconProps>(
  ({ $defaultIconColor, theme: { size } }) => ({
    color: $defaultIconColor,
    height: size.units(2),
    width: size.units(2),
  }),
);

interface IconWrapperProps {
  $size: Size;
  $isEndIcon?: boolean;
}

export const IconWrapper = styled.div<IconWrapperProps>(
  ({ $isEndIcon, $size, theme }) => ({
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',

    [StyledIcon]: {
      margin: $isEndIcon ? getEndIconMargin($size, theme) : getIconMargin($size, theme),
    },
  }),
);

interface WrapperProps extends HTMLAttributes<HTMLElement> {
  $disabled: boolean;
  $loading: boolean;
  $variant: Variant;
  $width: string;
  $height: string;
  $primaryColor?: ColorStyles;
  $secondaryColor?: ColorStyles;
  $iconColor?: Partial<ColorStyles>;
  $ellipsis?: boolean;
}

export const Wrapper = styled.button<WrapperProps>(
  ({
    theme,
    $variant,
    $disabled,
    $loading,
    $width,
    $height,
    $primaryColor,
    $secondaryColor,
    $iconColor,
    $ellipsis = false,
  }) => {
    return {
      position: 'relative',
      width: $width,
      minWidth: $ellipsis ? undefined : $width,
      height: $height,
      minHeight: $height,
      cursor: getCursor($loading, $disabled),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      maxWidth: $ellipsis ? '100%' : undefined,

      ...resetButtonStyles,

      ...style.mixins.increaseSpecificity({
        borderRadius: theme.size.units(5.5),
      }),

      [Background]: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
      },

      [SpinnerWrapper]: {
        position: 'absolute',
      },

      [StyledIcon]: {
        color: $disabled ? $iconColor?.disabled : undefined,
      },

      '&:hover': {
        [Background]: {
          ...getBackgroundHoverStyles($variant, $disabled, $loading, $primaryColor),
          ...getBackgroundBorderHoverStyles($variant, $disabled, $primaryColor),
        },
        [ContentWrapper]: {
          ...getContentHoverStyles($variant, $disabled, $primaryColor, $secondaryColor),
        },
        [StyledIcon]: {
          color: $iconColor?.hover,
        },
      },

      '&:active': {
        pointerEvents: $disabled ? 'none' : 'auto',

        [Background]: {
          transform: !$disabled && !$loading ? 'scale(.95)' : undefined,
        },
      },
    };
  },
);

export const ChildrenWrapper = styled.div<{ $ellipsis?: boolean }>(
  ({ $ellipsis = false }) => ({
    display: 'block',

    ...($ellipsis && {
      ...style.mixins.ellipsis(true),
    }),

    '&::first-letter': {
      textTransform: 'uppercase',
    },
  }),
);
